import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, inject, input, viewChildren } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { finalize, Observable, startWith, Subject, take } from 'rxjs'
import { Select } from 'primeng/select'
import { CommonModule } from '@angular/common'
import { FormArray, FormControl, ReactiveFormsModule } from '@angular/forms'
import { Card } from 'primeng/card'
import { ButtonDirective } from 'primeng/button'
import { Tag } from 'primeng/tag'
import { SharedModule } from 'primeng/api'
import { Panel } from 'primeng/panel'
import { DetailviewNavigationService, NavSection } from '@services/detailview-navigation.service'
import { TemplateContentUnit } from '../../models/template.model'
import { TemplateSidebarBridgeService } from '@features/template/services/template-sidebar-bridge.service'
import { AlbertModalService } from '@services/albert-modal.service'

@Component({
  selector: 'albert-template-cu-list',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, Card, ButtonDirective, Tag, Select, Panel, SharedModule],
  templateUrl: './template-cu-list.component.html',
  styleUrl: './template-cu-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DetailviewNavigationService]
})
export class TemplateCuListComponent {
  public readonly navControl = new FormControl('0')
  public readonly navService = inject(DetailviewNavigationService)
  public readonly formArray = input.required<FormArray<FormControl<TemplateContentUnit | null>>>()
  public readonly showDetails = input<boolean>(false)
  public readonly viewMode = input<boolean>(false)
  protected readonly bridgeService = inject(TemplateSidebarBridgeService)
  private readonly unitElements = viewChildren<ElementRef<HTMLElement>>('unitEl')
  private readonly destroyRef = inject(DestroyRef)
  private readonly modalService = inject(AlbertModalService)

  constructor() {
    afterNextRender(() => {
      this.navService.initialize(this.navControl, this.unitElements)
    })

    effect(() => {
      const array = this.formArray()
      array.valueChanges.pipe(
        startWith(array.value),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.refreshNav())
    })
  }

  public move(index: number, direction: number): void {
    const arr = this.formArray()
    const newIndex = index + direction

    if (newIndex < 0 || newIndex >= arr.length) return

    const currentControl = arr.at(index)
    arr.removeAt(index)
    arr.insert(newIndex, currentControl)

    this.refreshNav()
    this.updatePositions()
    this.formArray().markAsDirty()

    requestAnimationFrame(() => {
      this.navService.goToSection(newIndex)
    })
  }

  public moveTo(currentIndex: number, targetDisplayIndex: string | number): void {
    const arr = this.formArray()
    let targetIndex = Number(targetDisplayIndex) - 1

    if (Number.isNaN(targetIndex)) return

    if (targetIndex < 0) targetIndex = 0
    if (targetIndex >= arr.length) targetIndex = arr.length - 1

    if (currentIndex === targetIndex) return

    const control = arr.at(currentIndex)
    arr.removeAt(currentIndex)
    arr.insert(targetIndex, control)

    this.refreshNav()
    this.updatePositions()
    this.formArray().markAsDirty()

    requestAnimationFrame(() => {
      this.navService.goToSection(targetIndex)
    })
  }

  public getHighlightedContent(content: string | undefined | null): string {
    if (content === undefined || content === null || content === '') return ''
    return content.replaceAll(/##([^#\s]+)##/g, '<span class="code-placeholder">$1</span>')
  }

  public confirmDelete(currentIndex: number): Observable<boolean> {
    const decision$ = new Subject<boolean>()
    this.modalService.show({
      title: 'Delete Content Unit from Template',
      message: `Are you sure you want to delete the Content Unit "${this.formArray().at(currentIndex).value?.contentUnit.title}" from this template?`,
      type: 'warning',
      actions: [
        { label: 'No', position: 'left', styleClass: 'p-button-text btn-secondary', callback: () => decision$.next(false) },
        { label: 'Yes', position: 'right', styleClass: 'p-button-danger btn-secondary', callback: () => decision$.next(true) },
      ]
    })
    return decision$.pipe(take(1), finalize(() => this.modalService.hide()))
  }

  public deleteCu(currentIndex: number): void {
    this.confirmDelete(currentIndex).subscribe(confirmed => {
      if (confirmed) {
        this.bridgeService.removeContentUnit(this.formArray().at(currentIndex).value?.contentUnit.uuid ?? '')
        this.formArray().removeAt(currentIndex)
        this.updatePositions()
        this.formArray().markAsDirty()
      }
    })
  }

  private refreshNav(): void {
    const sections = this.formArray().controls.map((ctrl, index): NavSection => {
      const val = ctrl.value as TemplateContentUnit
      return {
        label: val?.contentUnit?.title ?? 'Untitled Unit',
        mainIndex: index
      }
    })

    this.navService.setSectionHeadings(sections)
  }

  private updatePositions(): void {
    this.formArray().controls.forEach((control, index) => {
      const currentValue = control.value
      if (currentValue) {
        control.patchValue({ ...currentValue, position: index }, { emitEvent: false })
      }
    })
  }

}
